import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PRODUCTS } from "../mock";
import { useAllProducts } from "../hooks/useProducts";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import { ShoppingCart, Heart, Truck, ShieldCheck, RefreshCw, ChevronRight, Minus, Plus, Star, Check, ImageOff } from "lucide-react";
import ProductCard from "../components/ProductCard";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const allProducts = useAllProducts();
  const product = allProducts.find((p) => p.id === id);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { toast } = useToast();

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-[#0b2960]">Produit introuvable</h1>
        <Link to="/boutique" className="mt-4 inline-block text-[#e11d24] hover:underline">Retour à la boutique</Link>
      </div>
    );
  }

  const related = allProducts.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    addItem(product, qty);
    toast({ title: "Ajouté au panier", description: `${qty} × ${product.name}` });
  };

  const buyNow = () => {
    addItem(product, qty);
    navigate("/panier");
  };

  return (
    <div>
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center text-sm text-neutral-600 gap-1">
          <Link to="/" className="hover:text-[#0b2960]">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/boutique" className="hover:text-[#0b2960]">Boutique</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/boutique?cat=${product.category}`} className="hover:text-[#0b2960] capitalize">{product.category.replace("-", " ")}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#0b2960] font-semibold truncate">{product.brand}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid md:grid-cols-2 gap-10">
        <div>
          <div className="relative bg-white border border-neutral-200 rounded-xl overflow-hidden aspect-square">
            {(product.gallery && product.gallery[activeImg]) || product.image ? (
              <img src={product.gallery[activeImg] || product.image} alt={product.name} className="w-full h-full object-contain p-4" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400 gap-3 bg-gradient-to-br from-neutral-100 to-neutral-200">
                <ImageOff className="h-16 w-16" />
                <span className="text-xs uppercase tracking-widest font-semibold">Photo en attente</span>
                <span className="text-[11px] text-neutral-500 max-w-xs text-center">Ce produit est disponible en magasin. Contactez-nous pour voir la photo.</span>
              </div>
            )}
            {product.discount > 0 && (
              <Badge className="absolute top-3 left-3 bg-[#e11d24] text-white text-sm font-bold px-3 py-1.5">-{product.discount}%</Badge>
            )}
          </div>
          {product.gallery && product.gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {product.gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square bg-white border-2 rounded-md overflow-hidden ${activeImg === i ? "border-[#0b2960]" : "border-neutral-200"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#e11d24]">{product.brand}</div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0b2960] mt-1 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
            </div>
            <span className="text-sm text-neutral-500">SKU : {product.sku}</span>
          </div>

          <div className="mt-5 flex items-end gap-3">
            <span className="text-4xl font-black text-[#e11d24]">{product.price.toFixed(2)} €</span>
            {product.oldPrice > product.price && (
              <span className="text-lg text-neutral-400 line-through mb-1">{product.oldPrice.toFixed(2)} €</span>
            )}
            {product.discount > 0 && (
              <span className="ml-2 text-xs bg-[#e11d24]/10 text-[#e11d24] font-bold px-2 py-1 rounded mb-1">Économisez {(product.oldPrice - product.price).toFixed(2)} €</span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1">TVA incluse · hors livraison</div>

          <p className="mt-5 text-neutral-700 leading-relaxed">{product.description}</p>

          <div className="mt-5 flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-green-700 font-semibold">En stock · {product.stock} disponible{product.stock > 1 ? "s" : ""}</span>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="inline-flex items-center border border-neutral-300 rounded-md">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center hover:bg-neutral-100"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="h-11 w-11 flex items-center justify-center hover:bg-neutral-100"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={handleAdd} className="flex-1 h-11 bg-[#0b2960] hover:bg-[#081e47] text-white font-bold">
              <ShoppingCart className="h-4 w-4 mr-2" /> Ajouter au panier
            </Button>
            <Button variant="outline" className="h-11 w-11 border-neutral-300 text-neutral-700 hover:text-[#e11d24] p-0">
              <Heart className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={buyNow} className="mt-3 w-full h-11 bg-[#e11d24] hover:bg-[#b8171d] text-white font-bold">
            Acheter maintenant
          </Button>

          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-neutral-200 pt-6">
            <div className="flex flex-col items-center text-center gap-1">
              <Truck className="h-5 w-5 text-[#0b2960]" />
              <span className="text-xs font-semibold text-neutral-700">Livraison rapide</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1">
              <ShieldCheck className="h-5 w-5 text-[#0b2960]" />
              <span className="text-xs font-semibold text-neutral-700">Garantie</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1">
              <RefreshCw className="h-5 w-5 text-[#0b2960]" />
              <span className="text-xs font-semibold text-neutral-700">Retours 14j</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-10">
        <Tabs defaultValue="desc" className="w-full">
          <TabsList className="bg-white border border-neutral-200">
            <TabsTrigger value="desc">Description</TabsTrigger>
            <TabsTrigger value="specs">Caractéristiques</TabsTrigger>
            <TabsTrigger value="delivery">Livraison</TabsTrigger>
          </TabsList>
          <TabsContent value="desc" className="bg-white border border-neutral-200 rounded-lg p-6 mt-2 text-neutral-700 leading-relaxed">
            {product.description} Ce produit {product.condition === "Occasion" ? "reconditionné" : "neuf"} est idéal pour équiper votre foyer avec des produits de qualité à prix imbattable. Bénéficiez de nos conseils personnalisés en magasin.
          </TabsContent>
          <TabsContent value="specs" className="bg-white border border-neutral-200 rounded-lg p-6 mt-2">
            <ul className="divide-y divide-neutral-200">
              {Object.entries(product.specs).map(([k, v]) => (
                <li key={k} className="flex justify-between py-3 text-sm">
                  <span className="text-neutral-500">{k}</span>
                  <span className="font-semibold text-neutral-800">{v}</span>
                </li>
              ))}
              <li className="flex justify-between py-3 text-sm">
                <span className="text-neutral-500">État</span>
                <span className="font-semibold text-neutral-800">{product.condition}</span>
              </li>
            </ul>
          </TabsContent>
          <TabsContent value="delivery" className="bg-white border border-neutral-200 rounded-lg p-6 mt-2 text-sm text-neutral-700 space-y-3">
            <p><b>Livraison Marseille & alentours :</b> livraison sous 48h ouvrées après validation de la commande.</p>
            <p><b>Retrait en magasin :</b> gratuit au 304 Avenue de la Capelette, 13010 Marseille.</p>
            <p><b>Retours :</b> 14 jours pour changer d'avis (produits neufs uniquement).</p>
          </TabsContent>
        </Tabs>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <h2 className="text-xl md:text-2xl font-extrabold text-[#0b2960] mb-4">Produits similaires</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
